import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Modal, ModalType } from '@/components/Modal'

interface ModalState {
  isOpen: boolean
  title: string
  message: string
  type: ModalType
  onConfirm?: () => void
  confirmText?: string
}

export const ProposalDetail: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [content, setContent] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  })

  const showModal = (title: string, message: string, type: ModalType = 'info', onConfirm?: () => void, confirmText?: string) => {
    setModal({ isOpen: true, title, message, type, onConfirm, confirmText })
  }

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }))
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['proposal', id],
    queryFn: async () => {
      const response = await api.get(`/proposals/${id}`)
      return response.data.data
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      await api.put(`/proposals/${id}`, data)
    },
    onSuccess: () => {
      refetch()
      setIsEditing(false)
    },
    onError: (error: any) => {
      showModal(t('common.error'), error?.response?.data?.error || 'Failed to update proposal', 'error')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/proposals/${id}`)
    },
    onSuccess: () => {
      closeModal()
      navigate('/proposals')
    },
    onError: (error: any) => {
      showModal(t('common.error'), error?.response?.data?.error || 'Failed to delete proposal', 'error')
    },
  })

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      await api.put(`/proposals/${id}`, { status })
    },
    onSuccess: () => {
      closeModal()
      refetch()
    },
    onError: (error: any) => {
      showModal(t('common.error'), error?.response?.data?.error || 'Failed to update proposal', 'error')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.noResults')}</div>
      </div>
    )
  }

  const proposal = data
  const canEdit = proposal.status === 'draft' || proposal.status === 'used'
  const hasContent = Boolean(proposal.content)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-500/20 text-gray-400'
      case 'used': return 'bg-green-500/20 text-green-400'
      case 'archived': return 'bg-yellow-500/20 text-yellow-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  // Inline markdown formatting (bold, italic) for any text fragment
  const renderInlineMarkdown = (text: string, keyPrefix: string) => {
    // Split by bold markers while keeping delimiters, then process each chunk
    const boldParts = text.split(/(\*\*.*?\*\*)/).filter(Boolean)
    let index = 0
    return boldParts.map((boldPart) => {
      if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
        const inner = boldPart.slice(2, -2)
        return <strong key={`${keyPrefix}-b-${index++}`}>{renderInlineMarkdownSimple(inner, `${keyPrefix}-inner`)}</strong>
      }
      return <span key={`${keyPrefix}-s-${index++}`}>{renderInlineMarkdownSimple(boldPart, `${keyPrefix}-simple`)}</span>
    })
  }

  const renderInlineMarkdownSimple = (text: string, keyPrefix: string) => {
    // Italic: *text* (avoid matching double asterisks handled above)
    const parts = text.split(/(\*[^*\n]+?\*)/).filter(Boolean)
    let index = 0
    return parts.map((part) => {
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <em key={`${keyPrefix}-i-${index++}`}>{part.slice(1, -1)}</em>
      }
      return <span key={`${keyPrefix}-t-${index++}`}>{part}</span>
    })
  }

  // Markdown table rendering: | col | col | rows with a |---| separator
  const renderTable = (tableLines: string[], keyPrefix: string) => {
    const rows = tableLines
      .filter((l) => !/^\|[\s\-|:]+\|$/.test(l)) // skip the |---|---| separator row
      .map((l) => l.split('|').slice(1, -1).map((cell) => cell.trim()))
    if (rows.length === 0) return null
    const [header, ...body] = rows
    return (
      <div key={keyPrefix} className="overflow-x-auto mb-4">
        <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-[#1a1a25]">
              {header.map((cell, ci) => (
                <th key={ci} className="px-3 py-2 text-left font-semibold border-b border-white/10">
                  {renderInlineMarkdown(cell, `${keyPrefix}-h-${ci}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri} className="border-b border-white/5 last:border-0">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 align-top">
                    {renderInlineMarkdown(cell, `${keyPrefix}-r${ri}-${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Simple markdown-like rendering (headings, lists, bold/italic, tables)
  const renderContent = (text: string) => {
    if (!text) return <p className="text-gray-400">{t('proposals.noContent')}</p>
    const lines = text.split('\n')
    const elements: React.ReactNode[] = []
    let i = 0
    while (i < lines.length) {
      const line = lines[i]
      // Markdown table: a block of consecutive lines starting with |
      if (line.trim().startsWith('|')) {
        const tableLines: string[] = []
        const startIndex = i
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          tableLines.push(lines[i].trim())
          i++
        }
        elements.push(renderTable(tableLines, `table-${startIndex}`))
        continue
      }
      if (line.startsWith('# ')) elements.push(<h1 key={i} className="text-2xl font-bold mb-4">{renderInlineMarkdown(line.slice(2), `h1-${i}`)}</h1>)
      else if (line.startsWith('## ')) elements.push(<h2 key={i} className="text-xl font-semibold mb-3">{renderInlineMarkdown(line.slice(3), `h2-${i}`)}</h2>)
      else if (line.startsWith('### ')) elements.push(<h3 key={i} className="text-lg font-medium mb-2">{renderInlineMarkdown(line.slice(4), `h3-${i}`)}</h3>)
      else if (line.startsWith('- ')) elements.push(<li key={i} className="ml-4 mb-1">{renderInlineMarkdown(line.slice(2), `li-${i}`)}</li>)
      else if (line.trim() === '') elements.push(<br key={i} />)
      else elements.push(<p key={i} className="mb-2">{renderInlineMarkdown(line, `p-${i}`)}</p>)
      i++
    }
    return elements
  }

  return (
    <div>
      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={t('common.cancel')}
        isLoading={deleteMutation.isPending}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <button
            onClick={() => navigate('/proposals')}
            className="text-sm text-gray-400 hover:text-white mb-2 flex items-center gap-1"
          >
            {t('proposals.backToProposals')}
          </button>
          <h1 className="text-3xl font-bold">{t('proposals.proposalFor', { name: proposal.lead_name })}</h1>
          <p className="text-gray-400 mt-1">{proposal.lead_email}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <button
              onClick={() => {
                setContent(proposal.content)
                setIsEditing(!isEditing)
              }}
              className="px-4 py-2 bg-white/5 text-gray-300 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
            >
              {isEditing ? t('common.cancel') : t('common.edit')}
            </button>
          )}
          {proposal.status === 'draft' && (
            <button
              onClick={() => statusMutation.mutate('used')}
              disabled={statusMutation.isPending}
              className="px-4 py-2 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg hover:bg-green-500/20 transition-colors disabled:opacity-50"
            >
              {t('proposals.markAsUsed')}
            </button>
          )}
          {proposal.status === 'archived' && (
            <button
              onClick={() => statusMutation.mutate('draft')}
              disabled={statusMutation.isPending}
              className="px-4 py-2 bg-white/5 text-gray-300 border border-white/10 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              {t('proposals.restore')}
            </button>
          )}
          {hasContent && (
            <>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(proposal.content)
                    .then(() => showModal(t('common.success'), t('proposals.copySuccess'), 'info'))
                    .catch(() => showModal(t('common.error'), t('proposals.downloadError'), 'error'))
                }}
                className="px-4 py-2 bg-white/5 text-gray-300 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
              >
                {t('proposals.copyProposalText')}
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([proposal.content], { type: 'text/markdown' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `proposal-${proposal.lead_name || 'draft'}.md`
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  URL.revokeObjectURL(url)
                }}
                className="px-4 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors"
              >
                {t('proposals.downloadMarkdown')}
              </button>
            </>
          )}
          {proposal.status !== 'archived' && (
            <button
              onClick={() => {
                showModal(
                  t('proposals.archiveTitle'),
                  t('proposals.archiveConfirm'),
                  'confirm',
                  () => statusMutation.mutate('archived'),
                  t('proposals.archive')
                )
              }}
              disabled={statusMutation.isPending}
              className="px-4 py-2 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-lg hover:bg-yellow-500/20 transition-colors disabled:opacity-50"
            >
              {t('proposals.archive')}
            </button>
          )}
          <button
            onClick={() => {
              showModal(
                t('proposals.deleteTitle') || 'Delete Proposal',
                t('proposals.deleteConfirm'),
                'confirm',
                () => deleteMutation.mutate(),
                t('common.delete')
              )
            }}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
          >
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="mb-6 p-4 rounded-lg bg-[#006cd4]/10 border border-[#006cd4]/20 text-[#006cd4] text-sm">
        {t('proposals.qualityHint')}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Content */}
        <div className="lg:col-span-2">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{t('proposals.content')}</h2>
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                {t(`proposals.${proposal.status}`)}
              </span>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full h-96 px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4] font-mono text-sm"
                  placeholder={t('proposals.contentPlaceholder')}
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => updateMutation.mutate({ content })}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                  >
                    {updateMutation.isPending ? t('proposals.saving') : t('common.save')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="prose prose-invert max-w-none">
                {renderContent(proposal.content)}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Info */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-4">{t('proposals.statusSidebar')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">{t('proposals.current')}</span>
                <span className={`${proposal.status === 'used' ? 'text-green-400' : proposal.status === 'archived' ? 'text-yellow-400' : 'text-white'}`}>
                  {t(`proposals.${proposal.status}`)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('proposals.created')}</span>
                <span className="text-white text-sm">{new Date(proposal.created_at).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">{t('proposals.updated')}</span>
                <span className="text-white text-sm">{new Date(proposal.updated_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Lead Info */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-4">{t('proposals.lead')}</h3>
            <button
              onClick={() => navigate(`/leads/${proposal.lead_id}`)}
              className="w-full px-4 py-2 bg-[#006cd4]/20 text-[#006cd4] rounded-lg hover:bg-[#006cd4]/30 transition-colors"
            >
              {t('proposals.viewLead')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
